import supabase from '../supabase/supabase';
import { templates } from './emailTemplates';

export const emailService = {
  sanitizeEmail: (email) => {
    if (!email) return '';
    return email.trim().toLowerCase();
  },

  sendEmail: async (payload) => {
    try {
      const sanitizedTo = Array.isArray(payload.to)
        ? payload.to.map(email => emailService.sanitizeEmail(email))
        : [emailService.sanitizeEmail(payload.to)];

      const fromEmail = import.meta.env.VITE_FROM_EMAIL || "team@email.bookotg.com";

      const requestBody = {
        from: `BookOTG Team <${fromEmail}>`,
        to: sanitizedTo,
        subject: payload.subject,
        html: payload.html
      };

      console.log('[EmailService] Invoking Supabase Edge Function...');

      // 1. Try the Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: requestBody
      });

      if (error) {
        console.warn("[EmailService] Edge Function Error, falling back to direct API call...", error);
        return await emailService.fallbackDirectSend(requestBody);
      }

      if (data && data.success === false) {
         console.warn("[EmailService] Provider Error:", data.error, data.details);
         return { success: false, error: data.error || "Email provider rejected the request.", details: data.details };
      }

      console.log(`[EmailService] Success via Edge Function:`, data);
      return { success: true, data };

    } catch (error) {
      console.error("[EmailService] Exception:", error);
      // Failsafe fallback
      return await emailService.fallbackDirectSend(payload);
    }
  },

  // 2. Direct API Fallback (Bypasses Edge Functions if they aren't deployed)
  fallbackDirectSend: async (requestBody) => {
    console.log('[EmailService] Attempting direct client-side EmailIt API call...');
    try {
      const apiKey = import.meta.env.VITE_EMAILIT_API_KEY || 'secret_vWQ2zpHDBoHdTzjl2R6rSxRupNaWA1aZ';
      
      // EXACT OFFICIAL ENDPOINT
      const response = await fetch('https://api.emailit.com/v2/emails', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify(requestBody)
      });
      
      const rawResponse = await response.text();
      let parsedResponse = {};
      try { parsedResponse = JSON.parse(rawResponse); } catch { parsedResponse = { raw: rawResponse }; }
      
      if (response.ok) {
        console.log('[EmailService] Direct API Success:', parsedResponse);
        return { success: true, data: parsedResponse };
      } else {
        console.error('[EmailService] Direct API Failed:', parsedResponse);
        return { success: false, error: parsedResponse.message || parsedResponse.error || "Direct EmailIt API Error", details: parsedResponse };
      }
    } catch (e) {
      console.error('[EmailService] Direct API Exception:', e);
      return { success: false, error: "Failed to send email via both Edge Function and Fallback API." };
    }
  },

  sendWelcomeEmail: async (name, role, email) => {
    const html = templates.welcomeEmail(name, role);
    return await emailService.sendEmail({
      to: email,
      subject: `Welcome to the Elite: Your BookOTG Journey Begins!`,
      html
    });
  },

  sendBookingReceivedToBarber: async (details) => {
    const html = templates.bookingReceived(details);
    return await emailService.sendEmail({
      to: details.barberEmail,
      subject: `Action Required: New Request`,
      html
    });
  },

  sendBookingConfirmedToCustomer: async (details) => {
    const html = templates.bookingConfirmed(details);
    return await emailService.sendEmail({
      to: details.customerEmail,
      subject: `Your Booking is Confirmed!`,
      html
    });
  },

  sendBookingCancelled: async (details, targetEmail) => {
    const html = templates.bookingCancelled(details);
    return await emailService.sendEmail({
      to: targetEmail,
      subject: `Urgent: Appointment Cancelled`,
      html
    });
  },

  sendBookingCompleted: async (details) => {
    const html = templates.bookingCompleted(details);
    return await emailService.sendEmail({
      to: details.customerEmail,
      subject: `Thank you for choosing BookOTG!`,
      html
    });
  },

  sendPromotionalEmail: async (email, subject, bodyHtml) => {
    const html = templates.promotionalEmail(subject, bodyHtml);
    return await emailService.sendEmail({
      to: email,
      subject,
      html
    });
  }
};